import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router'
import { SearchBox } from '@tecuity/granite-ui/SearchBox'
import { SelectBox } from '@tecuity/granite-ui'
import { ContextBar } from '@tecuity/granite-ui/ContextBar'
import { ContextButton } from '@tecuity/granite-ui/ContextButton'
import Cross from '@tecuity/sos-icons/react/cross-heavy'
import DateRow from 'components/DateRow/DateRow'
import { Spinner } from '../../Loaders/Loaders'
import { FormSelectionIcon } from '../../WorkviewManager/PaperProcessModel/FormSelectionList'
import ICONS from '../../Icons/Icons'
import * as api from '../../../requests'
import { SET_RECORD_TO_AMEND } from '../Workview2Reducer'
import { SUBROUTES } from '../../../App'
import '../../WorkviewManager/WorkviewManager.scss'

const SEARCH_TYPE_OPTIONS = [
  { value: 'contains', label: 'Contains' },
  { value: 'startswith', label: 'Starts With' },
]

const MiscEntitySelection = ({ moduleId, onRecordSelected }) => {
  const [template, setTemplate] = useState(null)
  const [templateLoading, setTemplateLoading] = useState(false)
  const [filterValues, setFilterValues] = useState({})
  const [results, setResults] = useState([])
  const [resultsLoading, setResultsLoading] = useState(false)
  const [searchError, setSearchError] = useState(null)
  const [selectedRecord, setSelectedRecord] = useState(null)
  const [recordNumValue, setRecordNumValue] = useState('')
  const [hasSearchBeenMade, setHasSearchBeenMade] = useState(false)
  const [forms, setForms] = useState([])
  const [formsLoading, setFormsLoading] = useState(false)
  const [formFilter, setFormFilter] = useState('')
  const dispatch = useDispatch()
  const history = useHistory()

  useEffect(() => {
    setFilterValues({})
    setResults([])
    setSearchError(null)
    setSelectedRecord(null)
    setRecordNumValue('')
    setHasSearchBeenMade(false)
    setFormFilter('')
    setTemplateLoading(true)
    api.GET_MISC_PAPER_SEARCH_TEMPLATE(
      moduleId,
      (data) => {
        setTemplate(data)
        setTemplateLoading(false)
      },
      () => {
        setTemplateLoading(false)
      }
    )
  }, [moduleId])

  useEffect(() => {
    onRecordSelected && onRecordSelected(selectedRecord)
    if (selectedRecord === null) {
      setFormFilter('')
      return
    }
    setFormsLoading(true)
    api.GET_MISC_PAPER_AMENDMENT_FORMS(
      moduleId,
      selectedRecord.RecordId,
      (data) => {
        setForms(data)
        setFormsLoading(false)
      },
      () => {
        setFormsLoading(false)
      }
    )
  }, [selectedRecord])

  const handleSearch = () => {
    setHasSearchBeenMade(true)
    setResultsLoading(true)
    setSearchError(null)
    api.GET_MISC_PAPER_PROCESS_SEARCH_RESULTS(
      moduleId,
      filterValues,
      recordNumValue,
      (data) => {
        setResults(data.Results || [])
        setResultsLoading(false)
      },
      (err) => {
        setSearchError(err && err.Message ? err.Message : 'An error occurred.')
        setResultsLoading(false)
      }
    )
  }

  const handleClear = () => {
    setFilterValues({})
    setResults([])
    setSearchError(null)
    setRecordNumValue('')
    setHasSearchBeenMade(false)
  }

  const renderField = (field) => {
    const dataType = field.DataType
    const fieldName = field.FieldName

    if (dataType === 'String') {
      return (
        <div key={fieldName}>
          <SelectBox
            label={field.DisplayName}
            aria-label={fieldName + '__comparator'}
            options={SEARCH_TYPE_OPTIONS}
            value={filterValues[fieldName + '__comparator'] || 'contains'}
            onChange={(val) => setFilterValues((prev) => ({ ...prev, [fieldName + '__comparator']: val }))}
          />
          <SearchBox
            showClearButton={false}
            showSearchButton={false}
            placeholder={field.DisplayName}
            value={filterValues[fieldName] || ''}
            onChange={(val) => setFilterValues((prev) => ({ ...prev, [fieldName]: val }))}
            onSubmit={handleSearch}
          />
        </div>
      )
    }

    if (dataType === 'Name' || dataType === 'Address' || dataType === 'PhoneNumber') {
      return (
        <div key={fieldName}>
          <SearchBox
            label={field.DisplayName}
            showClearButton={false}
            showSearchButton={false}
            placeholder={field.DisplayName}
            value={filterValues[fieldName] || ''}
            onChange={(val) => setFilterValues((prev) => ({ ...prev, [fieldName]: val }))}
            onSubmit={handleSearch}
          />
        </div>
      )
    }

    if (dataType === 'Bool') {
      const boolOptions = [
        { value: '', label: 'Any' },
        { value: 'true', label: 'Yes' },
        { value: 'false', label: 'No' },
      ]
      return (
        <div key={fieldName}>
          <SelectBox
            label={field.DisplayName}
            options={boolOptions}
            value={filterValues[fieldName] || ''}
            onChange={(val) => setFilterValues((prev) => ({ ...prev, [fieldName]: val || '' }))}
          />
        </div>
      )
    }

    if (dataType === 'Date' || dataType === 'DateTime') {
      return (
        <div key={fieldName}>
          <span>{field.DisplayName}</span>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
            <DateRow
              rowLabel={field.DisplayName + ' Start'}
              date={filterValues[fieldName + 'Start'] || ''}
              setDate={(val) => setFilterValues((prev) => ({ ...prev, [fieldName + 'Start']: val || null }))}
            />
            <DateRow
              rowLabel={field.DisplayName + ' End'}
              date={filterValues[fieldName + 'End'] || ''}
              setDate={(val) => setFilterValues((prev) => ({ ...prev, [fieldName + 'End']: val || null }))}
            />
          </div>
        </div>
      )
    }

    if (dataType === 'LookupType') {
      let lookupOptions = []
      try {
        lookupOptions = JSON.parse(field.LookupTypeValuesJson || '[]')
      } catch (e) {
        lookupOptions = []
      }
      if (!Array.isArray(lookupOptions)) {
        lookupOptions = []
      }
      const selectOptions = [{ value: '', label: 'Any' }].concat(
        lookupOptions.map((opt) => ({ value: String(opt.value), label: opt.label }))
      )
      return (
        <div key={fieldName}>
          <SelectBox
            label={field.DisplayName}
            options={selectOptions}
            value={filterValues[fieldName] || ''}
            onChange={(val) => setFilterValues((prev) => ({ ...prev, [fieldName]: val || '' }))}
          />
        </div>
      )
    }

    return null
  }

  const getTitle = (item) => {
    if (template && template.MainFieldKey && item.Fields && item.Fields[template.MainFieldKey] != null) {
      return item.Fields[template.MainFieldKey]
    }
    if (item.Fields) {
      const firstKey = Object.keys(item.Fields)[0]
      return firstKey != null ? item.Fields[firstKey] : null
    }
    return null
  }

  const getAdditionalColumns = () => {
    if (!template || !template.Columns) return []
    return template.Columns.filter(
      (col) =>
        col.Key !== 'RecordNum' &&
        col.Key !== 'RegistrationDate' &&
        col.Key !== template.MainFieldKey
    )
  }

  if (templateLoading) {
    return <Spinner className="page-loader" themeColor="neutral" />
  }

  if (selectedRecord) {
    const allMiscModules = (SUBROUTES || [])
      .filter((r) => r.IsMiscRecordsType)
      .flatMap((r) => r.MiscRecordsModules || [])
    const moduleEntry = allMiscModules.find((m) => m.MiscellaneousModuleId === moduleId)
    const moduleIcon = moduleEntry ? ICONS[moduleEntry.FormsIcon] : undefined
    const filteredForms = forms.filter(
      (f) => !formFilter || f.FormName.toLowerCase().includes(formFilter.toLowerCase())
    )

    return (
      <div>
        <div style={{ textAlign: 'center', minWidth: '400px', padding: '15px', display: 'flex', justifyContent: 'center' }}>
          <ContextBar elevation="high">
            <ContextButton
              leftIcon={<div className="button-icon"><Cross /></div>}
              variant="danger"
              onClick={() => setSelectedRecord(null)}
              customCss=""
            >
              Back
            </ContextButton>
            <span className="primary">
              <h1 style={{ fontWeight: 'bold' }}>Amending:</h1>{' '}
              <span>{getTitle(selectedRecord)}</span>
              {selectedRecord.RecordNum && <span style={{ marginLeft: '8px' }}>{selectedRecord.RecordNum}</span>}
            </span>
          </ContextBar>
        </div>
        {formsLoading && <Spinner className="page-loader" themeColor="neutral" />}
        {!formsLoading && (
          <div className="filingSelectButtonWrapper" style={{ marginTop: '8px' }}>
            <SearchBox
              placeholder="Filter by form name"
              value={formFilter}
              onChange={(e) => setFormFilter(e)}
              showClearButton={false}
              showSearchButton={false}
            />
          </div>
        )}
        {!formsLoading && forms.length === 0 && (
          <p>No amendment forms configured for this module.</p>
        )}
        {!formsLoading && forms.length > 0 && filteredForms.length === 0 && (
          <div style={{ textAlign: 'center', width: '100%', marginTop: '7.5em', marginBottom: '7.5em' }}>
            <h1 style={{ padding: '15px', fontWeight: 'bold' }}>No forms match filtration criteria...</h1>
          </div>
        )}
        {!formsLoading && filteredForms.length > 0 && (
          <div className="icon-wrapper">
            <div className="form-blocks paper-processed-wizard-item-wrapper">
              {filteredForms.map((form, i) => (
                <FormSelectionIcon
                  key={i}
                  formName={form.FormName}
                  value={form.FormDefinitionId}
                  icon={moduleIcon}
                  processInitialFiling={() => {
                    dispatch({ type: SET_RECORD_TO_AMEND, record: selectedRecord })
                    history.push('/office/workview/miscellaneous/process/' + form.FormDefinitionId + '/' + selectedRecord.RecordNum)
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div>
      <div className="filingSelectButtonWrapper">
        <SearchBox
          label="Record #"
          showClearButton={false}
          showSearchButton={false}
          placeholder="Record #"
          value={recordNumValue}
          onChange={(val) => setRecordNumValue(val)}
          onSubmit={handleSearch}
        />
        {template && (template.SearchableFields || []).map(renderField)}
        <div className="search-bar">
          <ContextButton variant="danger" onClick={handleClear} customCss="">Clear</ContextButton>
          <ContextButton variant="primary" onClick={handleSearch} customCss="">Search</ContextButton>
        </div>
      </div>
      {resultsLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '7.5em', marginBottom: '7.5em' }}>
          <Spinner className="page-loader" themeColor="neutral" />
        </div>
      )}
      {searchError && <div>{searchError}</div>}
      {results.length === 0 && !resultsLoading && !searchError && (
        <div style={{ textAlign: 'center', width: '100%', marginTop: '7.5em', marginBottom: '7.5em' }}>
          <h1 style={{ padding: '15px', fontWeight: 'bold' }}>
            {hasSearchBeenMade ? 'No Records Found - Please Refine Search' : 'Please Search for a Record to Amend'}
          </h1>
        </div>
      )}
      <div className="search-wrapper paper-process-result-wrapper">
        {results.map((item, i) => (
          <div
            key={i}
            className="stacked-data-result paper-process-result-item"
            onClick={() => setSelectedRecord(item)}
          >
            <div className="search-result">
              <span className="primary">{getTitle(item)}</span>
              <span className="secondary">
                {((template && template.Columns && (template.Columns.find(c => c.Key === 'RecordNum') || {}).Label) || 'Record #')}: {item.RecordNum}
              </span>
              {getAdditionalColumns().map((col, j) =>
                item.Fields && item.Fields[col.Key] != null ? (
                  <span key={j} className="secondary">
                    {col.Label}: {item.Fields[col.Key]}
                  </span>
                ) : null
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default MiscEntitySelection
